<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50">
    <div class="w-full h-screen bg-white overflow-hidden flex flex-col md:flex-row">
      
      <!-- LEFT PANEL -->
      <div class="w-full md:w-[45%] p-8 lg:p-16 flex flex-col justify-center">
        <div class="mb-10">
          <img src="https://www.datafuse.fr/assets/logo_datafuse_automation%20(4).svg" alt="Dimotec" class="h-10 mb-8">
          <h1 class="text-3xl font-extrabold text-gray-900 tracking-tight">Espace Gestion</h1>
          <p class="text-gray-500 mt-2 font-medium">Accédez à votre plateforme d'administration client.</p>
        </div>

        <!-- FORMULAIRE -->
        <form @submit.prevent="handleSubmit" class="space-y-6">
          
          <!-- EMAIL -->
          <div v-if="!resettingPassword || resetStep === 1" class="space-y-2">
            <label :class="{'text-blue-700': emailFocus}" class="text-xs font-bold uppercase tracking-widest text-gray-400">
              Identifiant Professionnel
            </label>
            <input 
              type="email" 
              placeholder="nom@entreprise.fr" 
              v-model="email"
              @focus="emailFocus = true" 
              @blur="emailFocus = false"
              required
              class="w-full px-5 py-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white transition-all focus:ring-2 focus:ring-blue-200 text-sm font-semibold"
            >
          </div>

          <!-- MOT DE PASSE NORMAL -->
          <div v-if="!resettingPassword" class="space-y-2">
            <div class="flex justify-between items-center">
              <label :class="{'text-blue-700': passwordFocus}" class="text-xs font-bold uppercase tracking-widest text-gray-400">
                Mot de passe
              </label>
              <button type="button" class="text-[11px] font-bold text-blue-800 hover:underline" @click="startReset">
                Oublié ?
              </button>
            </div>
            <input 
              type="password" 
              placeholder="••••••••" 
              v-model="password"
              @focus="passwordFocus = true" 
              @blur="passwordFocus = false"
              required
              class="w-full px-5 py-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white transition-all focus:ring-2 focus:ring-blue-200 text-sm font-semibold"
            >
          </div>

          <!-- REINITIALISATION MOT DE PASSE -->
          <div v-if="resettingPassword && resetStep === 1" class="space-y-2">
            <label class="text-xs font-bold uppercase tracking-widest text-gray-400">Entrez votre email pour réinitialisation</label>
            <input 
              type="email" 
              v-model="resetEmail"
              required
              class="w-full px-5 py-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white transition-all focus:ring-2 focus:ring-blue-200 text-sm font-semibold"
            >
          </div>

          <div v-if="resettingPassword && resetStep === 2" class="space-y-2">
            <label class="text-xs font-bold uppercase tracking-widest text-gray-400">Nouveau mot de passe</label>
            <input 
              type="password" 
              v-model="newPassword"
              placeholder="Minimum 8 caractères, 1 maj, 1 chiffre, 1 spécial"
              required
              class="w-full px-5 py-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white transition-all focus:ring-2 focus:ring-blue-200 text-sm font-semibold"
            >
            <label class="text-xs font-bold uppercase tracking-widest text-gray-400">Confirmez le mot de passe</label>
            <input 
              type="password" 
              v-model="confirmPassword"
              required
              class="w-full px-5 py-4 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white transition-all focus:ring-2 focus:ring-blue-200 text-sm font-semibold"
            >
            <p v-if="passwordError" class="text-red-500 text-xs mt-1">{{ passwordError }}</p>
          </div>

          <!-- CHECKBOX uniquement pour login -->
          <div v-if="!resettingPassword" class="flex items-center gap-3 py-2">
            <input type="checkbox" id="remember" v-model="remember" class="w-4 h-4 rounded border-gray-300 text-blue-800 focus:ring-blue-800">
            <label for="remember" class="text-sm text-gray-500 font-medium select-none">Rester connecté</label>
          </div>

          <button type="submit" class="w-full bg-[#222222] text-white py-4 rounded-xl font-bold text-sm uppercase tracking-widest hover:bg-black transition-all shadow-lg active:scale-[0.98]">
            {{ resettingPassword ? (resetStep === 1 ? 'Envoyer le lien' : 'Réinitialiser') : 'Se Connecter' }}
          </button>
        </form>

        <div class="mt-12 pt-8 border-t border-gray-100">
          <p class="text-xs text-gray-400 font-medium">
            Besoin d'aide ou problème d'accès ? <br>
            Contactez le support technique au <span class="text-blue-800">01 85 11 02 21</span>
          </p>
        </div>
      </div>

            <!-- RIGHT PANEL -->
      <div class=" md:flex  bg-[#222222] relative overflow-hidden flex-col justify-between p-16">
        <div class="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-800 rounded-full opacity-10 blur-[100px]"></div>
        <div class="absolute bottom-[-5%] left-[-5%] w-[300px] h-[300px] bg-blue-800 rounded-full opacity-20 blur-[80px]"></div>

        <div class="relative z-10">
          <div class="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full border border-white/10 mb-8">
            <span class="w-2 h-2 bg-blue-800 rounded-full animate-pulse"></span>
            <span class="text-white text-[10px] font-bold uppercase tracking-widest">Système de file d'attente v2.0</span>
          </div>
          <h2 class="text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-6">
            Pilotez votre <span class="text-blue-800">infrastructure</span> en temps réel.
          </h2>
        </div>

        <div class="relative z-10 grid grid-cols-2 gap-4">
          <div class="glass-panel p-6 rounded-2xl">
            <p class="text-blue-800 font-bold text-lg mb-1">Borne APK</p>
            <p class="text-gray-400 text-xs">Statut des impressions thermiques et connexion WebSocket.</p>
          </div>
          <div class="glass-panel p-6 rounded-2xl">
            <p class="text-blue-800 font-bold text-lg mb-1">Affichage TV</p>
            <p class="text-gray-400 text-xs">Gestion multi-écrans et synchronisation dynamique.</p>
          </div>
          <div class="glass-panel p-6 rounded-2xl col-span-2">
            <div class="flex justify-between items-center mb-4">
              <p class="text-white font-bold">Admin client : MEDIA LINK SOLUTIONS</p>
              <span class="text-[10px] bg-green-500/20 text-green-400 px-2 py-1 rounded border border-green-500/30">ACTIF</span>
            </div>
            <div class="h-2 w-full bg-white/10 rounded-full overflow-hidden">
              <div class="h-full bg-blue-800 w-[70%]"></div>
            </div>
          </div>
        </div>

        <div class="relative z-10 flex justify-between items-end mt-12">
          <div>
            <p class="text-white/40 text-[10px] uppercase font-bold tracking-widest">Développement & Maintenance</p>
            <p class="text-white font-bold">DATAFUSE / Enzo Viana</p>
          </div>
          <img src="https://www.datafuse.fr/assets/logo_datafuse_automation%20(4).svg" class="h-8 brightness-0 invert opacity-50">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from "../main"; // ← utiliser le toast exporté

export default {
  name: "LoginPage",
  data() {
    return {
      email: "",
      password: "",
      remember: false,
      emailFocus: false,
      passwordFocus: false,
      resettingPassword: false,
      resetStep: 1,
      resetEmail: "",
      newPassword: "",
      confirmPassword: "",
      passwordError: ""
    };
  },
  methods: {
async login() {
  console.log("Login déclenché");
  try {
    const response = await fetch("https://api.datafuse.fr/api/super-admin/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: this.email, password: this.password })
    });

    const data = await response.json();
    console.log("Réponse backend :", data);

    if (!response.ok) {
      toast.error(data.error || "Erreur de connexion");
      return;
    }

    // Stocke toujours le token
    localStorage.setItem("superAdminToken", data.token);

    toast.success("Connexion réussie ! Token stocké.");

    this.$router.push("/");
  } catch (err) {
    console.error(err);
    toast.error("Erreur serveur, impossible de se connecter.");
  }
},


    startReset() {
      this.resettingPassword = true;
      this.resetStep = 1;
      this.resetEmail = this.email;
    },

    handleSubmit() {
      if (!this.resettingPassword) {
        this.login();
      } else if (this.resetStep === 1) {
        const reinitialisation = true; // simulation
        if (reinitialisation) {
          this.resetStep = 2;
          toast.info("Lien de réinitialisation envoyé !");
        }
      } else if (this.resetStep === 2) {
        const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=<>?]).{8,}$/;
        if (!regex.test(this.newPassword)) {
          this.passwordError = "Le mot de passe doit contenir au moins 8 caractères, 1 majuscule, 1 chiffre et 1 caractère spécial.";
          toast.error(this.passwordError);
          return;
        }
        if (this.newPassword !== this.confirmPassword) {
          this.passwordError = "Les mots de passe ne correspondent pas.";
          toast.error(this.passwordError);
          return;
        }
        this.passwordError = "";
        toast.success(`Mot de passe réinitialisé pour ${this.resetEmail} !`);
        this.resettingPassword = false;
        this.resetStep = 1;
        this.resetEmail = "";
        this.newPassword = "";
        this.confirmPassword = "";
      }
    }
  }
};
</script>




<style>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

body {
  font-family: 'Plus Jakarta Sans', sans-serif;
  background-color: #FAFAFA;
}

.glass-panel {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
